import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import type { Task } from "@shared/schema";
import { useAuth } from "@/contexts/auth-context";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

export function TaskEditButton({ task, hovered = false }: { task: Task; hovered?: boolean }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [text, setText] = useState("");
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: ["/api/tree/snapshot"] }),
    client.invalidateQueries({ queryKey: ["/api/years"] }),
    client.invalidateQueries({ queryKey: ["/api/projects"] }),
    client.invalidateQueries({ queryKey: ["/api/tasks"] }),
    client.invalidateQueries({ queryKey: ["/api/pulses"] }),
    client.invalidateQueries({ queryKey: ["/api/pulses/batch-details"] }),
    client.invalidateQueries({ queryKey: ["/api/pulses/batch-metadata"] }),
    client.invalidateQueries({ queryKey: ["/api/pulses/batch/summary"] }),
  ]);
  const mutation = useMutation({
    mutationFn: async (action: "save" | "delete") => {
      await apiRequest(action === "delete" ? "DELETE" : "PATCH", `/api/tasks/${task.id}`,
        action === "save" ? { taskDescription: text.trim() } : undefined);
      return action;
    },
    onSuccess: async (action) => {
      setConfirmDelete(false);
      setOpen(false);
      toast({ title: action === "delete" ? "Task deleted" : "Task updated" });
      await refresh();
    },
    onError: (error: Error) => {
      toast({ title: "Task could not be changed", description: error.message, variant: "destructive" });
    },
  });
  if (user?.role !== "admin" && user?.role !== "team") return null;

  return <>
    <Button
      variant="ghost" size="sm" aria-label="Edit task" title="Edit task"
      className={`text-gray-400 hover:text-orange-400 p-1 h-auto ${hovered ? "opacity-100" : "opacity-0"} focus-visible:opacity-100 [@media(hover:none)]:opacity-100`}
      data-testid={`button-edit-task-${task.id}`}
      onClick={(event) => {
        event.stopPropagation();
        setText(task.taskDescription);
        setOpen(true);
      }}
    ><Pencil className="w-4 h-4" /></Button>
    <Dialog open={open} onOpenChange={(next) => { if (!mutation.isPending) setOpen(next); }}>
      <DialogContent className="bg-gray-900 border-gray-700 text-gray-100" onClick={event => event.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Edit task</DialogTitle>
          <DialogDescription>Update the task text or permanently delete this task.</DialogDescription>
        </DialogHeader>
        <Label htmlFor={`task-description-${task.id}`}>Task description</Label>
        <Textarea id={`task-description-${task.id}`} value={text} onChange={event => setText(event.target.value)}
          disabled={mutation.isPending} rows={5} className="bg-gray-800 border-gray-600" />
        <div className="flex justify-between gap-2">
          <Button variant="destructive" disabled={mutation.isPending} onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-4 h-4 mr-2" />Delete task
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" disabled={mutation.isPending} onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={mutation.isPending || !text.trim()} onClick={() => mutation.mutate("save")}>
              {mutation.isPending ? "Working…" : "Save"}
            </Button>
          </div>
        </div>
        <AlertDialog open={confirmDelete} onOpenChange={next => { if (!mutation.isPending) setConfirmDelete(next); }}>
          <AlertDialogContent onClick={event => event.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this task?</AlertDialogTitle>
              <AlertDialogDescription>This permanently deletes the task. Its parent pulse and other tasks will not be deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending}>Keep task</AlertDialogCancel>
              <AlertDialogAction disabled={mutation.isPending} onClick={event => {
                event.preventDefault();
                mutation.mutate("delete");
              }}>{mutation.isPending ? "Deleting…" : "Delete permanently"}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  </>;
}
